import type {
  DistributionMember,
  DistributionPreferences,
  DistributionResponse,
  DistributionSettings,
  DistributionUserGroupResponse,
  DistributionUserTag,
  Translations,
} from '@pbk-web/types';
import {
  ClientTagType,
  DistributionMemberStatus,
  DistributionSchedulingType,
  DistributionStatus,
  DistributionStatusFilter,
  PeopleType,
} from '@pbk-web/types';
import type { ParseKeys } from 'i18next';

import type { UserWithTags } from '../components/ownership/UsersSelect';
import type { ClientForm } from './ClientForm';
import type { DataExportPdfPreferencesRequest } from './DataExport';
import type { DistributionAccessCategoryGroupRequest } from './DistributionAssignment';

export { DistributionMemberStatus, DistributionSchedulingType, DistributionStatus, DistributionStatusFilter, PeopleType };
export type {
  DistributionMember,
  DistributionPreferences,
  DistributionResponse,
  DistributionSettings,
  DistributionUserGroupResponse,
  DistributionUserTag,
};

export type PeopleFilter = {
  pageSize?: number;
  pageNumber?: number;
  searchTerm?: string;
};

export type PeopleResponse = {
  id: string;
  firstName: string;
  lastName: string;
  type: PeopleType;
  email?: string;
  userImageId?: string;
};

export const GQLDistributionSchedulingToPlatformType: Record<string, DistributionSchedulingType> = {
  IMMEDIATE: DistributionSchedulingType.Immediate,
  EFFECTIVE_DATE: DistributionSchedulingType.EffectiveDate,
  FIXED_DATE: DistributionSchedulingType.FixedDate,
};

export const PlatformDistributionSchedulingToGQLType: Record<DistributionSchedulingType, string> = {
  [DistributionSchedulingType.Immediate]: 'IMMEDIATE',
  [DistributionSchedulingType.EffectiveDate]: 'EFFECTIVE_DATE',
  [DistributionSchedulingType.FixedDate]: 'FIXED_DATE',
};

export type DistributionCreateRequest = {
  clientFormId: string;
  clientFormVersion: number;
  settings: DistributionSettings;
  preferences: DistributionPreferences;
  /** Legacy explicit member list. OMIT when recipients are managed via `access` policies — sending an
   *  empty array makes the backend delete existing non-policy members (e.g. legacy individuals without a
   *  peopleType, or User tag-group members) that `/access` never returns. See PR #6090. */
  members?: DistributionMemberRequest[];
  /** Policy-based recipient assignment (Members + Address Book) — saved together with settings/preferences. */
  access?: DistributionAccessCategoryGroupRequest[];
};

export type DistributionUpdateRequest = {
  settings: DistributionSettings;
  preferences: DistributionPreferences;
  /** Legacy explicit member list. OMIT when recipients are managed via `access` policies — sending an
   *  empty array makes the backend delete existing non-policy members (e.g. legacy individuals without a
   *  peopleType, or User tag-group members) that `/access` never returns. See PR #6090. */
  members?: DistributionMemberRequest[];
  /** Policy-based recipient assignment (Members + Address Book) — saved together with settings/preferences. */
  access?: DistributionAccessCategoryGroupRequest[];
};

export type DistributionMemberRequest = {
  type: PeopleType;
  users: DistributionUserRequest[];
  tags: DistributionUserTagRequest[];
};

export type DistributionUserRequest = {
  addedAsIndividual: boolean;
  userId: string;
  tagIds: string[];
};

export type DistributionUserTagRequest = {
  id: string;
  synchronized: boolean;
};

export type DistributionUserTagResponse = {
  id: string;
  synchronized: boolean;
  translations: Translations<'name'>;
};

export type DistributionInfoForContactResponse = {
  clientForm: ClientForm;
  errors: DistributionInfoForContactErrorsResponse;
};

type DistributionInfoForContactErrorsResponse = {
  expired: boolean;
  acknowledgedOn: string | null;
};

export const PlatformDistributionMemberStatusToGQLType: Record<DistributionMemberStatus, string> = {
  [DistributionMemberStatus.Created]: 'CREATED',
  [DistributionMemberStatus.Pending]: 'PENDING',
  [DistributionMemberStatus.Acknowledged]: 'ACKNOWLEDGED',
  [DistributionMemberStatus.Signed]: 'SIGNED',
  [DistributionMemberStatus.Incomplete]: 'INCOMPLETE',
};

export const GQLDistributionMemberStatusToPlatformType: Record<string, DistributionMemberStatus> = {
  CREATED: DistributionMemberStatus.Created,
  PENDING: DistributionMemberStatus.Pending,
  ACKNOWLEDGED: DistributionMemberStatus.Acknowledged,
  SIGNED: DistributionMemberStatus.Signed,
  INCOMPLETE: DistributionMemberStatus.Incomplete,
};

export const DistributionMemberStatusKeys: Record<DistributionMemberStatus, ParseKeys<['distribution']>> = {
  [DistributionMemberStatus.Created]: 'distribution:status-names.created',
  [DistributionMemberStatus.Pending]: 'distribution:status-names.pending',
  [DistributionMemberStatus.Acknowledged]: 'distribution:status-names.acknowledged',
  [DistributionMemberStatus.Signed]: 'distribution:status-names.signed',
  [DistributionMemberStatus.Incomplete]: 'distribution:status-names.incomplete',
};

export const DistributionMemberStatusColors: Record<DistributionMemberStatus, string> = {
  [DistributionMemberStatus.Created]: 'text-text-2',
  [DistributionMemberStatus.Pending]: 'text-text-2',
  [DistributionMemberStatus.Acknowledged]: 'text-semantic-success',
  [DistributionMemberStatus.Signed]: 'text-semantic-success',
  [DistributionMemberStatus.Incomplete]: 'text-semantic-error',
};

export const DistributionStatusFilterKeys = {
  [DistributionStatusFilter.InActive]: 'distribution:filters.inactive',
  [DistributionStatusFilter.Active]: 'distribution:filters.active',
  [DistributionStatusFilter.Completed]: 'distribution:filters.completed',
  [DistributionStatusFilter.ActionNeededByCurrentUser]: 'distribution:filters.action',
} as const;

export type MemberRecipient = { id: string; type: PeopleType; fullname: string; userImageId?: string };

type EmailRecipient =
  | {
      members: MemberRecipient[];
      emailAddresses?: string[];
    }
  | {
      members?: MemberRecipient[];
      emailAddresses: string[];
    };

export type UncontrolledCopyRequest = {
  clientFormId: string;
  recipients: EmailRecipient;
  subject: string;
  message: string;
  includeSelfCopy: boolean;
} & DataExportPdfPreferencesRequest;

export const distributionUsersToRequestDto = (
  members: UserWithTags[],
  membersTags: DistributionUserTag[],
  contacts: UserWithTags[],
  contactsTags: DistributionUserTag[],
): DistributionMemberRequest[] => {
  return [
    {
      type: PeopleType.Member,
      users: members.map((user) => ({
        addedAsIndividual: user.addedAsIndividual ?? false,
        userId: user.id,
        tagIds: user.tagIds || [],
      })),
      tags: membersTags.map((tag) => ({
        id: tag.id,
        synchronized: tag.synchronized,
      })),
    },
    {
      type: PeopleType.Contact,
      users: contacts.map((user) => ({
        addedAsIndividual: user.addedAsIndividual ?? false,
        userId: user.id,
        tagIds: user.tagIds || [],
      })),
      tags: contactsTags.map((tag) => ({
        id: tag.id,
        synchronized: tag.synchronized,
      })),
    },
  ];
};

export const distributionMembersToRequestDto = (
  members: DistributionMember[],
  userGroups: DistributionUserGroupResponse[],
): DistributionMemberRequest[] => {
  const groupedByType = members.reduce<Record<PeopleType, DistributionMember[]>>(
    (acc, member) => {
      if (!acc[member.type]) acc[member.type] = [];
      acc[member.type].push(member);
      return acc;
    },
    {} as Record<PeopleType, DistributionMember[]>,
  );
  return Object.entries(groupedByType).map(([type, membersOfType]) => ({
    type: Number(type) as PeopleType,
    users: membersOfType.map((member) => ({
      addedAsIndividual: member.addedAsIndividual ?? false,
      userId: member.memberId,
      tagIds: member.tagIds || [],
    })),
    tags: userGroups
      .filter((group) => group.memberType === Number(type))
      .map((group) => ({
        id: group.tag.id,
        synchronized: group.synchronized,
      })),
  }));
};

export const distributionRequestDtoToMembers = (requests: DistributionMemberRequest[], distributionId?: string): DistributionMember[] => {
  return requests.flatMap((request) => {
    return request.users.map<DistributionMember>((user) => {
      return {
        distributionId: distributionId || '',
        memberId: user.userId,
        type: request.type,
        status: DistributionMemberStatus.Created,
        statusUtc: new Date().toISOString(),
        addedAsIndividual: user.addedAsIndividual,
        tagIds: user.tagIds,
      };
    });
  });
};

export const distributionRequestDtoToUserGroups = (requests: DistributionMemberRequest[]): DistributionUserGroupResponse[] => {
  const groups = requests.flatMap((request) =>
    request.tags.map((tag) => ({
      memberType: request.type,
      synchronized: tag.synchronized,
      tag: {
        id: tag.id,
        synchronized: tag.synchronized,
        // to be overridden by FE
        translations: {
          name: {},
        } as Translations<'name'>,
        type: ClientTagType.ContactGroup,
      },
    })),
  );

  // Distinct by tag.id
  const uniqueGroups = Object.values(
    groups.reduce<Record<string, DistributionUserGroupResponse>>((acc, group) => {
      acc[group.tag.id] = group;
      return acc;
    }, {}),
  );

  return uniqueGroups;
};
